/**
 * The ONE AFFDO card's adapter: a submitter's (scan method, reference level)
 * choice → the flow the server should compile, plus the parameters it binds
 * (fs#1798).
 *
 * **Why an adapter, and why here.** AFFDO is one parameterization; the scan
 * method and the reference level are choices inside it
 * (`AFFDO_SCAN_METHODS_PLAN.md`, FROZEN rev 6). Today the server still serves
 * that one thing as two flows that differ only in their scan node
 * (`full_affdo_sequential`, `full_affdo_wavefront`). The operator's direction
 * (2026-09-29) was to ship the single card NOW, ahead of that plan's single
 * `full_affdo` flow, as an adapter over the two. Everything that knows the two
 * flows exist lives in this file, so when `full_affdo` lands the rebind is:
 * {@link METHOD_FLOWS} names it for every method, {@link resolveAffdoChoice}
 * adds `scan_method` to the parameters, and the card does not change. Until the
 * rebind, a server that serves `full_affdo` alone gets plain template cards
 * ({@link partitionFlows}), not a card with nothing to run.
 *
 * **Refusals are stated, not discovered.** A combination AFFDO or FlowStack
 * refuses today is disabled on the card with its reason. Four of the five
 * refused reference levels would be refused at submit anyway, by
 * `affdo.dft_constrained_opt`'s `invalid_combinations` (afs#400/#411), but
 * `dft-sp` is refused only at RUNTIME (exit 3), after the scan has been paid
 * for — which is exactly the "submitted to fail later" this card exists to
 * prevent.
 *
 * Pure data and functions: no React, no fetching. The card and the submit page
 * read it; `affdo-flow-adapter.test.ts` pins every (method, level) pair.
 */

/** The scan methods of `AFFDO_SCAN_METHODS_PLAN.md` §3.1, in display order. */
export const SCAN_METHODS = ["directional", "bidirectional", "wavefront"] as const;
export type ScanMethod = (typeof SCAN_METHODS)[number];

/** AFFDO's seven reference levels, in the order its flows declare them
 * (`reference_level.values` on both runnable flows). */
export const REFERENCE_LEVELS = [
  "xtb",
  "dft-sp",
  "dft",
  "qdpi2-sp",
  "qdpi2",
  "mace-sp",
  "mace",
] as const;
export type ReferenceLevel = (typeof REFERENCE_LEVELS)[number];

/** The flow parameter the card owns. The generic parameter form must not
 * render a second control for it. */
export const CARD_OWNED_PARAMETERS = ["reference_level"] as const;

/** Every flow name of the AFFDO family. Only the ones the card can represent
 * fold into it (see {@link partitionFlows}): `full_affdo` is listed so the
 * page treats it as AFFDO, but until {@link METHOD_FLOWS} maps a method to it
 * it stays a plain template beside the card rather than vanishing into a card
 * that offers no way to run it (Rupert PR-1860 r2). */
export const AFFDO_FLOW_NAMES: readonly string[] = [
  "full_affdo_sequential",
  "full_affdo_wavefront",
  "full_affdo",
];

/** Which served flow runs each method today; `null` = no flow does. */
const METHOD_FLOWS: Record<ScanMethod, string | null> = {
  directional: "full_affdo_sequential",
  // The bidirectional xtb scan does not exist in FlowStack yet: it is built by
  // the scan-methods epic (plan §3.1, ATTMOS/project-manager#624).
  bidirectional: null,
  wavefront: "full_affdo_wavefront",
};

export const SCAN_METHOD_INFO: Record<ScanMethod, { label: string; description: string }> = {
  directional: {
    label: "Directional",
    description:
      "xtb, one leg from the seed angle through 360°. AFFDO's default scan (torsional_scan_engine xtb).",
  },
  bidirectional: {
    label: "Bidirectional",
    description:
      "xtb, a forward and a backward leg merged point by point, lower energy wins (AFFDO bi_directional_torsional_scan).",
  },
  wavefront: {
    label: "Wavefront",
    description:
      "FFPOpt wavefront scan on the ffpopt-torch image. Refuses a charged fragment, because AFFDO passes FFPOpt no charge.",
  },
};

export const REFERENCE_LEVEL_INFO: Record<ReferenceLevel, string> = {
  xtb: "The xtb scan is the reference; no refinement runs.",
  "dft-sp": "DFT single points on the scan geometries.",
  dft: "Constrained DFT optimisation at every scan point.",
  "qdpi2-sp": "QDπ2 single points on the scan geometries.",
  qdpi2: "Constrained QDπ2 optimisation at every scan point.",
  "mace-sp": "MACE single points on the scan geometries.",
  mace: "Constrained MACE optimisation at every scan point.",
};

/** Why each refused level cannot run today. Absent = runnable. */
const REFUSED_LEVELS: Partial<Record<ReferenceLevel, string>> = {
  "dft-sp":
    "Not runnable yet: no single-point refinement path exists (plan D4), and affdo.dft_constrained_opt refuses it only at runtime, after the scan has run.",
  "qdpi2-sp":
    "Not runnable yet: no ML refinement and no single-point path; refused at submit (afs#400).",
  qdpi2:
    "Not runnable yet: no ML refinement; the tile would run DFT and label it QDπ2, so it is refused at submit (afs#400).",
  "mace-sp":
    "Not runnable yet: no ML refinement and no single-point path; refused at submit (afs#400).",
  mace: "Not runnable yet: no ML refinement; the tile would run DFT and label it MACE, so it is refused at submit (afs#400).",
};

export type Availability = { available: true } | { available: false; reason: string };

export function isAffdoFlow(name: string | null | undefined): boolean {
  return name !== null && name !== undefined && AFFDO_FLOW_NAMES.includes(name);
}

/** Split the flows to RENDER into the AFFDO family (rendered as one card) and
 * the rest (rendered as before, in their original order).
 *
 * `servedFlows` is the COMPLETE list `/api/flows` returned, kept apart from
 * `flows` so a filtered or searched subset can never flip the card-or-not
 * decision (Rupert PR-1860 r2): that is decided from what the server serves,
 * and `flows` only says which of them are on screen.
 *
 * **The card needs a method to run.** When no scan method maps to any served
 * AFFDO flow — the server serves only `full_affdo` before this adapter is
 * rebound to it — the card would be inert, so there is no card: every flow,
 * AFFDO ones included, passes through as a plain template, as before fs#1798,
 * and AFFDO never vanishes from the list.
 *
 * **The card holds only what it can represent**: a flow a scan method maps
 * to. An AFFDO flow it has no method for (`full_affdo`, served beside the old
 * flows before the rebind) stays a plain template next to it, reachable. */
export function partitionFlows<T extends { name: string }>(
  servedFlows: readonly string[],
  flows: readonly T[]
): { affdo: T[]; others: T[] } {
  const affdo: T[] = [];
  const others: T[] = [];
  for (const flow of flows) (isCardFlow(flow.name, servedFlows) ? affdo : others).push(flow);
  return { affdo, others };
}

/** Does `name` belong to the AFFDO card on a server serving `servedFlows`?
 * False for a non-AFFDO flow, for an AFFDO flow the card cannot represent,
 * and for every AFFDO flow when the card falls back to plain templates (see
 * {@link partitionFlows}). */
export function isCardFlow(
  name: string | null | undefined,
  servedFlows: readonly string[]
): boolean {
  return (
    isAffdoFlow(name) && methodForFlow(name) !== null && defaultScanMethod(servedFlows) !== null
  );
}

/** Can this method be submitted against the flows the server serves? */
export function scanMethodAvailability(
  method: ScanMethod,
  servedFlows: readonly string[]
): Availability {
  const flow = METHOD_FLOWS[method];
  if (flow === null) {
    return {
      available: false,
      reason:
        "Not available yet: FlowStack has no bidirectional xtb scan. It arrives with the single full_affdo flow (scan-methods epic, project-manager#624).",
    };
  }
  if (!servedFlows.includes(flow)) {
    return { available: false, reason: `This server does not serve ${flow}.` };
  }
  return { available: true };
}

/** Can this reference level be submitted? `offered` is the level enum the
 * resolved flow declares; a level missing from it would be refused by the
 * compiler, so it is refused here with that reason instead. */
export function referenceLevelAvailability(
  level: ReferenceLevel,
  offered?: readonly unknown[]
): Availability {
  const refused = REFUSED_LEVELS[level];
  if (refused !== undefined) return { available: false, reason: refused };
  if (offered !== undefined && !offered.some((v) => String(v) === level)) {
    return { available: false, reason: "The selected flow does not offer this level." };
  }
  return { available: true };
}

/** The flow that runs this method today, or `null` when none does. Whether
 * the server actually serves it is {@link scanMethodAvailability}'s question. */
export function flowForMethod(method: ScanMethod): string | null {
  return METHOD_FLOWS[method];
}

/** The flow a served AFFDO flow name stands for, read back as a method.
 * `null` for an AFFDO flow no method maps to (`full_affdo` before the
 * rebind). */
export function methodForFlow(flowName: string | null | undefined): ScanMethod | null {
  for (const method of SCAN_METHODS) {
    if (METHOD_FLOWS[method] === flowName) return method;
  }
  return null;
}

/** The method the card opens on: AFFDO's default (directional) when it is
 * served, else the first one that is. `null` when none is. */
export function defaultScanMethod(servedFlows: readonly string[]): ScanMethod | null {
  return SCAN_METHODS.find((m) => scanMethodAvailability(m, servedFlows).available) ?? null;
}

export type AffdoResolution =
  | { ok: true; flowName: string; parameters: Record<string, unknown> }
  | { ok: false; reason: string };

/**
 * A (method, level) choice → the request the submit page sends.
 *
 * `level` is `undefined` when the flow's manifest declares no settable
 * `reference_level` (the page then submits none, as for any other hidden
 * parameter). The returned `parameters` are an OVERLAY on the page's own
 * parameter dict: today only `reference_level`; after the `full_affdo`
 * rebind, `scan_method` too.
 */
export function resolveAffdoChoice(
  choice: { method: ScanMethod | null; level: string | undefined },
  servedFlows: readonly string[],
  offeredLevels?: readonly unknown[]
): AffdoResolution {
  if (choice.method === null) return { ok: false, reason: "Pick a scan method." };
  const method = scanMethodAvailability(choice.method, servedFlows);
  if (!method.available) return { ok: false, reason: method.reason };
  const flowName = flowForMethod(choice.method) as string;
  if (choice.level === undefined) return { ok: true, flowName, parameters: {} };
  if (!(REFERENCE_LEVELS as readonly string[]).includes(choice.level)) {
    return { ok: false, reason: `Unknown reference level ${choice.level}.` };
  }
  const level = referenceLevelAvailability(choice.level as ReferenceLevel, offeredLevels);
  if (!level.available) return { ok: false, reason: `${choice.level}: ${level.reason}` };
  return { ok: true, flowName, parameters: { reference_level: choice.level } };
}

/** The manifest the generic parameter form renders: the card's parameters
 * removed, so no knob appears twice. */
export function withoutCardOwnedParameters(
  manifest: Record<string, unknown> | undefined
): Record<string, unknown> | undefined {
  if (!manifest) return manifest;
  const params = manifest.parameters;
  if (!params || typeof params !== "object") return manifest;
  const rest = { ...(params as Record<string, unknown>) };
  for (const name of CARD_OWNED_PARAMETERS) delete rest[name];
  return { ...manifest, parameters: rest };
}
